import type { Identifier } from "ra-core";

import { assertValidOptionLabel } from "../dealProfile/profileRules";
import type { ProfileOption } from "../types";

/**
 * Almikia Epic EF (TASK-121): pure, framework-free helpers backing the
 * direccion-only "Opciones del perfil" screen (ONE generic component shared
 * by the five identical-shape catalogues, DATA CONTRACT EF (1)). Label
 * validation and uniqueness are NOT reimplemented here -- they are the exact
 * same rule for every catalogue, already shared with the FakeRest provider
 * and the SQL guard via `dealProfile/profileRules.ts`'s
 * `assertValidOptionLabel`; this module only wraps it for a form (returns a
 * message instead of throwing) and adds the list-ordering and
 * error-passthrough helpers the screen needs. Mirrors the shape of
 * `activityCatalogue/activityTypeForm.ts` and `stageConfigs/stageConfigForm.ts`.
 */

export const PROFILE_OPTION_GENERIC_ERROR =
  "No se pudo guardar el cambio. Inténtalo de nuevo.";

/**
 * DATA CONTRACT EF (1): validates a catalogue option's `label` against the
 * SAME rule the server enforces (`assertValidOptionLabel`) and returns the
 * Spanish message instead of throwing, for inline form use. `existing` is
 * every OTHER option of the SAME catalogue -- the caller excludes the
 * record's own row before calling this (`selfId` is the extra safety net an
 * edit needs when the caller passes the full list unfiltered).
 */
export function validateProfileOptionLabel(
  label: string,
  existing: ReadonlyArray<{
    id: Identifier;
    label: string;
    archived_at?: string | null;
  }>,
  selfId: Identifier | undefined,
): string | undefined {
  try {
    assertValidOptionLabel(label, existing, selfId);
    return undefined;
  } catch (error) {
    return error instanceof Error
      ? error.message
      : PROFILE_OPTION_GENERIC_ERROR;
  }
}

/** Ascending `(position, id)` -- the shared tie-break used across every EF/EA/EP catalogue screen. */
function comparePositionThenId(
  a: { position: number; id: Identifier },
  b: { position: number; id: Identifier },
): number {
  if (a.position !== b.position) return a.position - b.position;
  return String(a.id).localeCompare(String(b.id), undefined, {
    numeric: true,
  });
}

/**
 * The catalogue list's row order (ticket acceptance criterion): active
 * (non-archived) options first, then archived ones, each group by
 * `(position, id)` -- mirrors `activityTypeForm.ts`'s
 * `orderActivityTypesForList`.
 */
export function orderProfileOptionsForList(
  options: readonly ProfileOption[],
): ProfileOption[] {
  const active = options
    .filter((option) => option.archived_at == null)
    .slice()
    .sort(comparePositionThenId);
  const archived = options
    .filter((option) => option.archived_at != null)
    .slice()
    .sort(comparePositionThenId);
  return [...active, ...archived];
}

/**
 * Maps a save/write error to the message shown to direccion. Every guard on
 * this write path (`assertValidOptionLabel`, the FakeRest catalogue
 * callbacks, the SQL trigger) already raises a plain Spanish sentence (DATA
 * CONTRACT EF (3)), so it passes through UNCHANGED -- only a message-less
 * error falls back to the generic message.
 */
export function toProfileOptionErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : undefined;
  return message || PROFILE_OPTION_GENERIC_ERROR;
}
